import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GSplatSystemConfiguration,
  GSplatSystemResourceFactory,
} from '@/plugins/gsplat/GSplatSystem'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { getGSplatUrlBasename, getGSplatUrlExtension } from '@/plugins/gsplat/loaders/GSplatHandler'
import type { GaussianSplatLoadRequest } from '@/plugins/gsplat/types/PluginTypes'
import { LoaderRegistry } from '@/runtime/LoaderRegistry'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

/** Compatibility provider 的选择模式；不得由 graphics backend 隐式决定。 */
export type GSplatCompatibilityMode = 'disabled' | 'legacy-advanced'

export interface GSplatCompatibilitySelection {
  readonly format: string | undefined
  readonly mode: Exclude<GSplatCompatibilityMode, 'disabled'>
}

export interface GSplatCompatibilityLoadContext extends GSplatCompatibilitySelection {
  readonly signal: AbortSignal
}

/**
 * 迁移期 WebGL golden 的可删除脚手架。
 *
 * 固定字面量防止 provider 演化成第二套 runtime；选择输入刻意不包含 graphics backend，返回值也只能是
 * 共享 Resource lease，不能创建公共 Handle、registry 或 Plugin 生命周期。
 */
export interface GSplatCompatibilityProvider {
  readonly id: string
  readonly scope: 'migration-webgl-golden'
  readonly selection: 'format-and-mode'
  readonly resourceModel: 'shared-resource-octree-placement'
  readonly removalGate: 'shared-sog-lod-resource-path'
  canLoad(
    request: Readonly<GaussianSplatLoadRequest>,
    selection: GSplatCompatibilitySelection
  ): boolean
  load(
    request: Readonly<GaussianSplatLoadRequest>,
    context: GSplatCompatibilityLoadContext
  ): Promise<GSplatResourceLease>
}

interface ResolvedLoader {
  load(
    request: Readonly<GaussianSplatLoadRequest>,
    context: LoadContext
  ): Promise<GSplatResourceLease>
}

/**
 * 对应旧 ModelLoader 的格式选择与 Resource 构造职责。
 * canonical loader 永远优先；compatibility provider 只在显式迁移模式且没有 canonical match 时生效。
 */
export class GSplatAssetLoadingService {
  private readonly loaders = new LoaderRegistry<GaussianSplatLoadRequest, GSplatResourceLease>()

  constructor(
    private readonly adapter: GSplatGraphicsAdapter,
    private readonly configuration: GSplatSystemConfiguration,
    private readonly resourceFactory?: GSplatSystemResourceFactory,
    private readonly compatibilityMode: GSplatCompatibilityMode = 'disabled',
    private readonly compatibilityProviders: readonly GSplatCompatibilityProvider[] = []
  ) {
    const ids = new Set<string>()
    for (const provider of compatibilityProviders) {
      assertCompatibilityProvider(provider)
      if (!provider.id.trim()) throw new Error('GSplat compatibility provider id 不能为空')
      if (ids.has(provider.id)) {
        throw new Error(`GSplat compatibility provider id "${provider.id}" 重复`)
      }
      ids.add(provider.id)
    }
  }

  register(loader: AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease>): void {
    this.loaders.register(loader)
  }

  resolve(request: Readonly<GaussianSplatLoadRequest>): ResolvedLoader {
    const canonical = request.format
      ? this.loaders.get(request.format)
      : this.loaders.detect(request)
    if (canonical) return canonical

    const provider = this.resolveCompatibilityProvider(request)
    if (provider) {
      const selection = this.createCompatibilitySelection(request)
      return {
        load: (source, context) => {
          const signal = context.signal ?? new AbortController().signal
          return provider.load(source, { ...selection, signal })
        },
      }
    }

    throw new Error(
      `GSplatSystem: 不支持 "${request.url}"，已注册格式: ${this.loaders.formats().join(', ')}`
    )
  }

  formats(): string[] {
    return this.loaders.formats()
  }

  assertResourceCount(numSplats: number): void {
    if (!Number.isSafeInteger(numSplats) || numSplats <= 0) {
      throw new Error(`GSplatSystem: 非法 splatCount，actual=${numSplats}`)
    }
    const limit = this.configuration.getConfig().maxSplats
    // 0 = 不限制：由调用端设置上限；实际显存占用仍由设备限制在分配时兜底。
    if (limit > 0 && numSplats > limit) {
      throw new Error(
        `GSplatSystem: splatCount 超出 maxSplats，actual=${numSplats}, limit=${limit}`
      )
    }
  }

  createResource(data: SplatData, signal?: AbortSignal): GSplatResourceLease {
    if (signal?.aborted) throw new DOMException('GSplat Resource 创建已取消', 'AbortError')
    this.assertResourceCount(data.numSplats)
    if (!this.resourceFactory) {
      throw new Error('GSplatSystem: 未配置 Unified Resource factory')
    }
    if (
      this.resourceFactory.graphicsAdapter &&
      this.resourceFactory.graphicsAdapter !== this.adapter
    ) {
      throw new Error('GSplatSystem: Resource factory 与 System 不属于同一 graphics device')
    }
    const resource = this.resourceFactory.createResource(data, { adapter: this.adapter, signal })
    try {
      if (signal?.aborted) throw new DOMException('GSplat Resource 创建已取消', 'AbortError')
      this.assertResourceAdapter(resource)
      if (resource.numSplats !== data.numSplats) {
        throw new Error(
          `GSplatSystem: Resource factory 返回数量不一致，expected=${data.numSplats}, actual=${resource.numSplats}`
        )
      }
      return resource
    } catch (error) {
      resource.dispose()
      throw error
    }
  }

  /** 在任何 Handle/Placement 发布前拒绝跨 device GPU Resource。 */
  assertResourceAdapter(resource: GSplatResourceLease): void {
    const declaredAdapter = resource.graphicsAdapter
    const formatAdapter = resource.format?.graphicsAdapter
    if (declaredAdapter && formatAdapter && declaredAdapter !== formatAdapter) {
      throw new Error('GSplatSystem: Resource 声明的 adapter 与 format 不一致')
    }
    const actualAdapter = declaredAdapter ?? formatAdapter
    if (actualAdapter && actualAdapter !== this.adapter) {
      throw new Error('GSplatSystem: Resource 与 System 不属于同一 graphics device')
    }
    if (resource.streams && !actualAdapter) {
      throw new Error('GSplatSystem: GPU-backed Resource 缺少 graphics adapter identity')
    }
  }

  private resolveCompatibilityProvider(
    request: Readonly<GaussianSplatLoadRequest>
  ): GSplatCompatibilityProvider | undefined {
    if (this.compatibilityMode === 'disabled') return undefined
    const selection = this.createCompatibilitySelection(request)
    const matches = this.compatibilityProviders.filter((provider) =>
      provider.canLoad(request, selection)
    )
    if (matches.length > 1) {
      throw new Error(
        `GSplatSystem: 多个 compatibility provider 匹配 (${matches.map(({ id }) => id).join(', ')})`
      )
    }
    return matches[0]
  }

  private createCompatibilitySelection(
    request: Readonly<GaussianSplatLoadRequest>
  ): GSplatCompatibilitySelection {
    if (this.compatibilityMode === 'disabled') {
      throw new Error('GSplatSystem: compatibility provider 未启用')
    }
    return Object.freeze({
      format: request.format?.trim() || inferFormat(request.url),
      mode: this.compatibilityMode,
    })
  }
}

function inferFormat(url: string): string | undefined {
  const basename = getGSplatUrlBasename(url)
  if (basename === 'lod-meta.json') return 'lod-meta'
  const extension = getGSplatUrlExtension(url)
  return extension || undefined
}

function assertCompatibilityProvider(provider: GSplatCompatibilityProvider): void {
  if (
    provider.scope !== 'migration-webgl-golden' ||
    provider.selection !== 'format-and-mode' ||
    provider.resourceModel !== 'shared-resource-octree-placement' ||
    provider.removalGate !== 'shared-sog-lod-resource-path'
  ) {
    throw new Error(`GSplat compatibility provider "${provider.id}" 违反迁移边界契约`)
  }
}
